/**
 * Account-level courier order sync (Pharmacy Settings → Shipping).
 * Scans the pharmacy's OWN e-Courier account (server-side range probe matched by
 * pickup-stop name), streams matches into rx_shipments as found (with Rx Ref#),
 * and shows the live activity log of what the scan is doing. Session-driven:
 * ticks run through the signed-in user's bound connection.
 */
import { useEffect, useState } from 'react'
import * as api from '../../api/client'

export default function SyncCourierOrders() {
  const [days, setDays] = useState(60)
  const [custom, setCustom] = useState(false)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [job, setJob] = useState<api.CarrierScanJob | null>(null)
  const [orders, setOrders] = useState<api.CarrierScanOrder[] | null>(null)
  const [importing, setImporting] = useState<{ done: number; total: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [log, setLog] = useState<api.CarrierScanLogEntry[]>([])
  const [showLog, setShowLog] = useState(true)

  // Log refresher: while a job is present, pull the newest activity entries.
  useEffect(() => {
    if (!job) { setLog([]); return }
    let stop = false
    const pull = async () => { try { const r = await api.listCarrierScanLog(job.id); if (!stop) setLog(r.items) } catch { /* keep last */ } }
    pull()
    const t = setInterval(pull, job.status === 'running' ? 4000 : 15000)
    return () => { stop = true; clearInterval(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.status])

  const [starting, setStarting] = useState(false)

  // Resume a running/most-recent job on open so a page refresh doesn't orphan it.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.listCarrierScans()
        const j = r.items?.[0]
        if (j && (j.status === 'running' || j.status === 'paused' || j.status === 'completed')) setJob(j)
      } catch { /* panel still usable */ }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Poll while running; load the order list once completed.
  useEffect(() => {
    if (!job) return
    if (job.status === 'completed') {
      api.listCarrierScanOrders(job.id).then(r => setOrders(r.items)).catch(() => {})
      return
    }
    if (job.status !== 'running') return
    // Drive the scan from this session: the background timer only fires while the
    // host is warm (alwaysOn=false cost posture), so each loop iteration TICKS one
    // batch through the caller's bound connection, then refreshes the counters.
    let stopped = false
    ;(async () => {
      while (!stopped) {
        try { await api.tickCarrierScan() } catch { /* transient; retry after status */ }
        try {
          const j = await api.getCarrierScanStatus(job.id)
          setJob(j)
          if (j.status !== 'running') break
        } catch { /* next iteration */ }
        await new Promise(res => setTimeout(res, 1500))
      }
    })()
    return () => { stopped = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.status, job?.cursor_at])

  const start = async () => {
    setStarting(true); setError(null); setOrders(null)
    try {
      const carriers = await api.listShippingCarriers()
      const active = carriers.items.find(c => c.is_active) ?? carriers.items[0]
      if (!active) { setError('No carrier account configured.'); return }
      const r = await api.startCarrierScan(active.id, custom && fromDate ? { from: fromDate, to: toDate || undefined } : { days })
      setJob({ id: r.job_id, status: 'running' })
    } catch (e: any) {
      setError(e?.payload?.message || e?.message || 'Could not start the scan.')
    } finally { setStarting(false) }
  }

  const pending = (orders ?? []).filter(o => !o.imported_shipment_id)
  const importAll = async () => {
    if (!pending.length) return
    setImporting({ done: 0, total: pending.length }); setError(null)
    let done = 0, failed = 0
    for (const o of pending) {
      try { await api.importCarrierShipment(o.carrier_order_number) }
      catch { failed++ }
      done++
      setImporting({ done, total: pending.length })
      await new Promise(res => setTimeout(res, 250)) // politeness to the courier API
    }
    setImporting(null)
    if (failed) setError(`${failed} of ${pending.length} orders failed to import — rerun to retry just those.`)
    if (job) { try { setOrders((await api.listCarrierScanOrders(job.id)).items) } catch { /* list reload below */ } }
  }

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 12, margin: '10px 0', fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong>Sync courier orders</strong>
        <span style={mutedText}>Scans this pharmacy&apos;s courier account; every matched order is imported server-side (with its Rx Ref#) so Rx search covers all of them. Runs even with this page closed.</span>
      </div>
      {error && <div style={{ ...errorBox, marginTop: 8 }}>{error}</div>}
      {!job || job.status === 'cancelled' || job.status === 'error' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <span style={mutedText}>Look back:</span>
          {[30, 60, 90, 120].map(d => (
            <button key={d} type="button" onClick={() => { setDays(d); setCustom(false) }}
              style={{ ...chipBtn, ...(!custom && days === d ? chipBtnActive : null) }}>
              {d}d
            </button>
          ))}
          <button type="button" onClick={() => setCustom(true)}
            style={{ ...chipBtn, ...(custom ? chipBtnActive : null) }}>
            Custom
          </button>
          {custom && (
            <>
              <input type="date" value={fromDate} max={toDate} onChange={e => setFromDate(e.target.value)}
                style={{ padding: '5px 8px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13 }} />
              <span style={mutedText}>to</span>
              <input type="date" value={toDate} min={fromDate} max={new Date().toISOString().slice(0, 10)}
                onChange={e => setToDate(e.target.value)}
                style={{ padding: '5px 8px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13 }} />
            </>
          )}
          <button type="button" style={createBtn} disabled={starting || (custom && !fromDate)} onClick={start}>
            {starting ? 'Starting…' : 'Start scan'}
          </button>
          <span style={mutedText}>Runs in the background; safe to leave this page.</span>
        </div>
      ) : job.status === 'running' ? (
        <div style={{ marginTop: 10 }}>
          Scanning… probed {job.probed ?? 0} order numbers · matched {job.matched ?? 0} for this pharmacy
          <div style={mutedText}>Matches are imported on the server as they are found — watch the activity log{importing ? ` — importing ${importing.done}/${importing.total}…` : ''}.</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" style={btnGhost} onClick={async () => {
              try { await api.pauseCarrierScan(job.id); setJob({ ...job, status: 'paused' }) }
              catch (e: any) { setError(e?.message || 'Could not pause.') }
            }}>Pause</button>
            <button type="button" style={{ ...btnGhost, color: 'var(--color-danger, #b42318)' }} onClick={async () => {
              if (!window.confirm('Cancel this scan? Progress and already-imported orders are kept; the remaining range will not be scanned.')) return
              try { await api.cancelCarrierScan(job.id); setJob(null); setOrders(null) }
              catch (e: any) { setError(e?.message || 'Could not cancel.') }
            }}>Cancel scan</button>
          </div>
        </div>
      ) : job.status === 'paused' ? (
        <div style={{ marginTop: 10 }}>
          Paused at {job.probed ?? 0} probed · {job.matched ?? 0} matched. Nothing runs while paused; progress is kept.
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" style={createBtn} onClick={async () => {
              try { await api.resumeCarrierScan(job.id); setJob({ ...job, status: 'running' }) }
              catch (e: any) { setError(e?.message || 'Could not resume.') }
            }}>Resume</button>
            <button type="button" style={{ ...btnGhost, color: 'var(--color-danger, #b42318)' }} onClick={async () => {
              if (!window.confirm('Cancel this scan? Progress and already-imported orders are kept; the remaining range will not be scanned.')) return
              try { await api.cancelCarrierScan(job.id); setJob(null); setOrders(null) }
              catch (e: any) { setError(e?.message || 'Could not cancel.') }
            }}>Cancel scan</button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 10 }}>
          <div>Scan complete — {orders ? `${orders.length} orders on this account · ${pending.length} not yet in OwlRx` : 'loading order list…'}</div>
          {orders && pending.length > 0 && !importing && (
            <button type="button" style={{ ...createBtn, marginTop: 8 }} onClick={importAll}>
              Import all {pending.length}
            </button>
          )}
          {importing && <div style={{ marginTop: 8 }}>Importing… {importing.done} / {importing.total}</div>}
          {orders && pending.length === 0 && <div style={{ ...mutedText, marginTop: 6 }}>Everything on the account is already in OwlRx.</div>}
          <button type="button" style={{ ...btnGhost, marginTop: 8, marginLeft: importing ? 0 : 8 }} onClick={() => { setJob(null); setOrders(null) }}>
            New scan
          </button>
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <button type="button" style={miniLink} onClick={() => setShowLog(v => !v)}>
          {showLog ? 'Hide activity log' : 'Show activity log'}
        </button>
        {showLog && (
          <div style={logBox}>
            {log.length === 0 && <div style={mutedText}>No activity yet{job ? '' : ' — start a scan to see the log'}.</div>}
            {log.map(e => (
              <div key={e.id} style={{ display: 'flex', gap: 8, padding: '2px 0', color: e.level === 'error' ? '#b42318' : e.level === 'warn' ? '#b45309' : 'inherit' }}>
                <span style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{new Date(e.at).toLocaleTimeString()}</span>
                <span>{e.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const btnGhost: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, background: 'transparent', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }
const mutedText: React.CSSProperties = { color: 'var(--color-text-muted)', fontSize: 13 }
const errorBox: React.CSSProperties = { padding: 8, background: '#dc26260d', border: '1px solid #dc262633', borderRadius: 6, fontSize: 12, color: '#dc2626' }
const createBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--color-primary, #0369a1)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }
const chipBtn: React.CSSProperties = { border: '1px solid var(--color-border)', background: 'none', borderRadius: 999, fontSize: 12, padding: '4px 10px', cursor: 'pointer', color: 'var(--color-text)' }
const chipBtnActive: React.CSSProperties = { background: 'var(--color-primary, #0369a1)', color: '#fff', borderColor: 'var(--color-primary, #0369a1)' }
const miniLink: React.CSSProperties = { border: 'none', background: 'none', color: 'var(--color-primary, #0369a1)', fontSize: 12, cursor: 'pointer', padding: 0 }
const logBox: React.CSSProperties = { marginTop: 6, maxHeight: 220, overflowY: 'auto', fontSize: 12, fontFamily: 'ui-monospace, monospace', border: '1px solid var(--color-border)', borderRadius: 6, padding: 8, background: 'var(--color-bg-subtle, #fafafa)' }
